<template>
  <v-form ref="ETABLISSEMENT_CONTACTS" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="ETABLISSEMENT_CONTACTS">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <contact-summary v-if="isDisplayed('REPRESENTANT_ID')"
                         ref="REPRESENTANT_ID"
                         :theme="theme"
                         :showMissing="showMissing"
                         v-model:value=representantId
                         :moduleId=moduleId
                         libelle="'Représentant'"
                         newLabel=''
                         selectText=""
                         @status=statusRepresentantId
                         :autoClear=true
                         :owner="owner"
                         :requiredAttributs="requiredAttributs"
                         :autoSelect=true
                         :ressource=ressourceContactSummary
                         :filters=representantIdFilter
                         :toggleReset=resetRepresentantId
                         :required="isRequired('REPRESENTANT_ID')"
                         :contextId=etablissement.id
                         :readOnly="readOnly('REPRESENTANT_ID')"
                         :objType=objType
                         :fiche=fiche
                         :sectionName="sectionName">
        </contact-summary>
        <etablissement-mail v-if="isDisplayed('MAIL')"
                            ref="MAIL"
                            :theme="theme"
                            :showMissing="showMissing"
                            v-model:value=mail
                            @status=statusMail
                            :over-ride-placeholder=placeholderMail
                            :required="isRequired('MAIL')"
                            :contextId=etablissement.id
                            :readOnly="readOnly('MAIL')"
                            :objType=objType
                            :fiche=fiche
                            :sectionName="sectionName">
        </etablissement-mail>
        <etablissement-numero v-if="isDisplayed('NUMERO')"
                              ref="NUMERO"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=numero
                              @status=statusNumero
                              :over-ride-placeholder=placeholderNumero
                              :required="isRequired('NUMERO')"
                              :contextId=etablissement.id
                              :readOnly="readOnly('NUMERO')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-numero>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudEtablissement from '@/generated/cruds/CrudEtablissement';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import Owner from '@/extend/classes/Owner';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudEtablissement,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.ETABLISSEMENT_CONTACTS,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderMail: null,
        placeholderNumero: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceContactSummary () { return RessourceGenControl(GenControl.ETABLISSEMENT_CONTACTS_CONTACT_SUMMARY); },

      ressource () { return RessourceGenSection(GenSection.ETABLISSEMENT_CONTACTS); },

      owner (): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.ETABLISSEMENT;
        owner.objId = this.editEtablissement.id;
        return owner;
      },

      fields (): string[] {
        return ['REPRESENTANT_ID', 'MAIL', 'NUMERO'];
      },

      extraButtons (): Button[] {
        return [];
      },

      representantIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editEtablissement.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("REPRESENTANT_ID", false);
          this.readOnlyMap.set("MAIL", false);
          this.readOnlyMap.set("NUMERO", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("REPRESENTANT_ID", true);
          this.displayMap.set("MAIL", true);
          this.displayMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("REPRESENTANT_ID", true);
          this.requiredMap.set("MAIL", true);
          this.requiredMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoContacts' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
